import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchTickets } from '../../store/slices/ticketsSlice';
import { fetchLocations } from '../../store/slices/locationsSlice';
import StatusBadge from '../../components/StatusBadge';
import { dedupeLocations } from '../../utils/locations';
import FilterBar from '../../components/FilterBar';
import client from '../../api/client';
import { businessMinutes, formatBusinessTime } from '../../utils/businessHours';

const STATUSES = ['Open', 'Ticket Assignment', 'Troubleshooting', 'Fault Confirmation', 'On Hold', 'RMA', 'Replacement', 'Configuration and Installation', 'Maintenance', 'Asset Review', 'Resolved', 'Recovery', 'Closed'];
const PAGE_SIZE = 20;

function ResolutionCell({ ticket, breachMinutes }) {
  const isClosed = ticket.status === 'Closed' || ticket.status === 'Resolved';
  const startDt = ticket.fault_reported_at || ticket.created_at;
  if (!isClosed || !ticket.resolved_at || !startDt) {
    return <span className="text-gray-300">—</span>;
  }
  const serviceStart = ticket.room_detail?.service_start_time?.slice(0, 5) || '09:00';
  const serviceEnd = ticket.room_detail?.service_end_time?.slice(0, 5) || '17:00';
  const minutes = businessMinutes(new Date(startDt), new Date(ticket.resolved_at), serviceStart, serviceEnd);
  if (!ticket.is_downtime) {
    // SLA only applies to downtime tickets — just show how long it took, no SLA verdict.
    return <span className="text-gray-700 font-medium whitespace-nowrap">{formatBusinessTime(minutes)}</span>;
  }
  const met = minutes <= breachMinutes;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-gray-700 font-medium whitespace-nowrap">{formatBusinessTime(minutes)}</span>
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${met ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
        {met ? 'SLA Met' : 'SLA Breached'}
      </span>
    </div>
  );
}

export default function TicketList() {
  const dispatch = useDispatch();
  const { list: tickets, total, loading } = useSelector(s => s.tickets);
  const { list: locations } = useSelector(s => s.locations);
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'ADMIN';

  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');
  const [downtimeOnly, setDowntimeOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [breachMinutes, setBreachMinutes] = useState(30);

  useEffect(() => {
    client.get('/config/escalation/').then(({ data }) => {
      setBreachMinutes(data.minutes_until_critical ?? 30);
    }).catch(() => {});
  }, []);

  useEffect(() => { dispatch(fetchLocations()); }, [dispatch]);

  useEffect(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (status) params.status = status;
    if (location) params.location = location;
    if (downtimeOnly) params.is_downtime = true;
    if (search) params.search = search;
    dispatch(fetchTickets(params));
  }, [dispatch, page, status, location, downtimeOnly, search]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const allCurrentSelected = tickets.length > 0 && tickets.every(t => selected.has(t.id));

  const toggleSelectAll = () => {
    if (allCurrentSelected) {
      setSelected(prev => { const next = new Set(prev); tickets.forEach(t => next.delete(t.id)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); tickets.forEach(t => next.add(t.id)); return next; });
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} ticket(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await client.post('/tickets/bulk-delete/', { ids: Array.from(selected) });
      setSelected(new Set());
      dispatch(fetchTickets({ page, page_size: PAGE_SIZE, status, location, is_downtime: downtimeOnly || undefined, search: search || undefined }));
    } catch {}
    setBulkDeleting(false);
  };

  const [exporting, setExporting] = useState(false);
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (downtimeOnly) params.append('is_downtime', 'true');
      const resp = await client.get(`/tickets/export/?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tickets_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export tickets.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">Tickets</h1>
          <p className="text-gray-400 text-sm mt-0.5">{total} total tickets</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && selected.size > 0 && (
            <button onClick={handleBulkDelete} disabled={bulkDeleting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50">
              {bulkDeleting
                ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
              }
              Delete {selected.size} ticket{selected.size > 1 ? 's' : ''}
            </button>
          )}
          <button onClick={handleExportExcel} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-brand-600 bg-brand-50 border border-brand-200 rounded-xl hover:bg-brand-100 transition-colors disabled:opacity-50">
            {exporting
              ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            }
            Download Excel
          </button>
          <Link to="/tickets/new" className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            New Ticket
          </Link>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        hasActiveFilters={!!(search || status || location || downtimeOnly)}
        onResetAll={() => { setSearch(''); setStatus(''); setLocation(''); setDowntimeOnly(false); setPage(1); }}
      >
        <FilterBar.Item value={search} onClear={() => { setSearch(''); setPage(1); }}>
          <div className="relative flex-1 min-w-48">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              type="text"
              placeholder="Search tickets..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="input-field pl-9"
            />
          </div>
        </FilterBar.Item>
        <FilterBar.Item value={status} onClear={() => { setStatus(''); setPage(1); }}>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="input-field w-auto">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FilterBar.Item>
        <FilterBar.Item value={location} onClear={() => { setLocation(''); setPage(1); }}>
          <select value={location} onChange={e => { setLocation(e.target.value); setPage(1); }} className="input-field w-auto">
            <option value="">All Locations</option>
            {dedupeLocations(locations).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </FilterBar.Item>
        <FilterBar.Item value={downtimeOnly} onClear={() => { setDowntimeOnly(false); setPage(1); }}>
          <label className={`flex items-center gap-2 text-sm font-medium cursor-pointer px-4 py-2.5 rounded-xl border transition-all duration-200 ${downtimeOnly ? 'bg-magenta-50 border-magenta-300 text-magenta-700' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-200'}`}>
            <input type="checkbox" checked={downtimeOnly} onChange={e => { setDowntimeOnly(e.target.checked); setPage(1); }} className="rounded accent-magenta-500" />
            Downtime Only
          </label>
        </FilterBar.Item>
      </FilterBar>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)'}}>
                {isAdmin && (
                  <th className="px-3 py-2.5">
                    <input type="checkbox" checked={allCurrentSelected} onChange={toggleSelectAll}
                      className="rounded accent-magenta-500 cursor-pointer" />
                  </th>
                )}
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Ticket #</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Subject</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Assigned To</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Device</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Room</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Location</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Resolution</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin w-7 h-7 text-magenta-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      <p className="text-gray-400 text-sm">Loading tickets...</p>
                    </div>
                  </td>
                </tr>
              ) : tickets.map(t => (
                <tr key={t.id} className={[
                  'transition-colors group',
                  t.escalation_level === 'critical' ? 'border-l-4 border-l-red-500 bg-red-50/40 hover:bg-red-50/60' :
                  t.escalation_level === 'at_risk'  ? 'border-l-4 border-l-amber-400 bg-amber-50/30 hover:bg-amber-50/50' :
                  selected.has(t.id) ? 'bg-brand-50/60 hover:bg-brand-50/40' : 'hover:bg-brand-50/40',
                ].join(' ')}>
                  {isAdmin && (
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)}
                        className="rounded accent-magenta-500 cursor-pointer" />
                    </td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {t.is_downtime && <span className="w-2 h-2 rounded-full bg-magenta-500 shrink-0" title="Downtime ticket" />}
                      <Link to={`/tickets/${t.id}`} className="text-magenta-500 font-bold hover:text-magenta-600 text-xs">{t.ticket_number}</Link>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-nowrap">
                      <StatusBadge status={t.status} />
                      {t.escalation_level === 'critical' && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />Crit
                        </span>
                      )}
                      {t.escalation_level === 'at_risk' && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />Risk
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-800 font-medium truncate">{t.subject}</td>
                  <td className="px-3 py-2 text-gray-500 truncate text-xs">{t.assigned_to_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 truncate">{t.device_detail?.device_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 truncate">{t.device_detail?.room?.room_name || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 truncate">{t.device_detail?.room?.location?.name || '—'}</td>
                  <td className="px-3 py-2 text-xs"><ResolutionCell ticket={t} breachMinutes={breachMinutes} /></td>
                  <td className="px-3 py-2 text-gray-400 whitespace-nowrap text-xs">{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
              {!loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-2xl">🎫</div>
                      <p className="text-brand-500 font-semibold">No tickets found</p>
                      <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-brand-50">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages} <span className="text-gray-300">·</span> {total} tickets
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary px-4 py-2 text-xs disabled:opacity-40"
              >
                ← Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary px-4 py-2 text-xs disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
